import React, { useCallback, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Drawer, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as Funnel } from 'src/Images/icons/common/funnel.svg';
import { ReactComponent as BulletPoints } from 'src/Images/icons/common/bulletpoints.svg';
import { ReactComponent as ExcelIcon } from 'src/Images/icons/common/excel.svg';
import { ReactComponent as Refresh } from 'src/Images/icons/common/refresh.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as Calendar } from 'src/Images/icons/common/calendar.svg';
import Table from 'src/components/Vehicle/List/Table';
import TableRightDrawer from 'src/components/Vehicle/List/TableRightDrawer';
import Breadcrumb from 'src/components/Breadcrumb';
import { CustomToolTip } from 'src/utils/CustomToolTip';
import getColorCode from 'src/utils/GetThemeColor';
import usePageTitle from 'src/utils/SetPageTitle';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import {
  getPlants,
  getVehicleTrackingWorkflows,
} from 'src/redux/Actions/VehicleTracking';
import { getOutwardReportWithExitTime } from 'src/redux/Actions/VehicleTrackingOutward';
import 'src/style/tableList.css';
import { useLocation } from 'react-router-dom';
import FilterMultiSelect from 'src/utils/FilterMultiSelect';
import { loadExcelExportModules } from 'src/utils/excelExport';
import OutwardReportMobileFilterDrawer from 'src/components/OutwardReport/OutwardReportMobileFilterDrawer';

const DEFAULT_FIELDS = ['1', '2', '3', '4', '5', '6', '7'];
const PREFERENCE_KEY = 'savedOutwardReportWithExitTimeColumns';

const today = new Date().toISOString().split('T')[0];
const emptyFilters = {
  startDate: today,
  endDate: today,
  plantId: '',
  workflowId: '',
};

function renderCellValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  if (typeof value === 'object') return value.name ?? '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return value;
}

function OutwardReportTableContent({ headers, rows }) {
  if (!rows.length) {
    return (
      <tr>
        <td colSpan={headers.length || 1} className="text-center text-grey">
          No records found
        </td>
      </tr>
    );
  }

  return rows.map((row) => (
    <tr key={row.id ?? row.vehicle_tracking_number}>
      {headers.map((header) => (
        <td key={header.columnId} className="text-sm">
          {renderCellValue(row[header.value])}
        </td>
      ))}
    </tr>
  ));
}

export default function OutwardReportList() {
  usePageTitle('Outward Report');
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:767px)');
  const authReducer = useSelector((state) => state.AuthReducer);
  const outwardReportWithExitTimeList = useSelector(
    (state) =>
      state.VehicleTrackingOutwardReducer.outwardReportWithExitTimeList,
  );
  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantOrganisationId = authReducer?.userData?.organisations?.id;
  const [pageNumber, setPageNumber] = useState(1);
  const [paginationBatchSize, setPaginationBatchSize] = useState(10);
  const [showFunnel, setShowFunnel] = useState(false);
  const [openOrganizer, setOpenOrganizer] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [formData, setFormData] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(true);

  const { plantsData, workflows } = useSelector(
    (state) => state.VehicleTrackingReducer,
  );

  const savedPreferences = useMemo(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PREFERENCE_KEY));
      return {
        selected: saved?.selected || DEFAULT_FIELDS,
        order: saved?.order || [],
      };
    } catch {
      return { selected: DEFAULT_FIELDS, order: [] };
    }
  }, []);
  const [selectedFieldFilter, setSelectedFieldFilter] = useState(
    savedPreferences.selected,
  );
  const [draggedFieldOrder, setDraggedFieldOrder] = useState(
    savedPreferences.order,
  );

  const fieldFilter = outwardReportWithExitTimeList?.fieldMapping || [];
  const rows = outwardReportWithExitTimeList?.outwardReport || [];
  const totalPages = outwardReportWithExitTimeList?.totalPages || 1;
  const selectedFields = outwardReportWithExitTimeList?.selectedFields || [];
  const effectiveFieldOrder =
    draggedFieldOrder.length > 0 ? draggedFieldOrder : selectedFieldFilter;

  const tableHeaders = useMemo(
    () =>
      effectiveFieldOrder
        .map((fieldId) => {
          const selectedField = selectedFields.find(
            (field) => String(field.id) === String(fieldId),
          );
          return selectedField
            ? { ...selectedField, id: fieldId, columnId: fieldId }
            : null;
        })
        .filter(Boolean),
    [effectiveFieldOrder, selectedFields],
  );

  useSetBreadcrumbData(location.pathname, 'Outward Report');

  const debouncedSearch = useCallback(() => {
    const timer = setTimeout(() => {
      setSearchInput(searchText);
      setPageNumber(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  React.useEffect(debouncedSearch, [debouncedSearch]);

  const fetchOutwardReport = useCallback(() => {
    if (!tenantOrganisationId) return;
    const params = new URLSearchParams();
    if (hasAppliedFilters) {
      if (appliedFilters.startDate) {
        params.append('start_date', appliedFilters.startDate);
      }
      if (appliedFilters.endDate) {
        params.append('end_date', appliedFilters.endDate);
      }
      if (appliedFilters.plantId) {
        params.append('plant_id', appliedFilters.plantId);
      }
      if (appliedFilters.workflowId) {
        params.append('workflow_id', appliedFilters.workflowId);
      }
    }

    if (searchInput) {
      params.append('search_term', searchInput);
    }

    dispatch(
      getOutwardReportWithExitTime(
        tenantOrganisationId,
        pageNumber,
        searchInput,
        null,
        null,
        effectiveFieldOrder,
        paginationBatchSize,
        params.toString(),
      ),
    );
  }, [
    appliedFilters,
    dispatch,
    effectiveFieldOrder,
    hasAppliedFilters,
    pageNumber,
    paginationBatchSize,
    refreshKey,
    searchInput,
    tenantOrganisationId,
  ]);

  React.useEffect(() => {
    fetchOutwardReport();
  }, [fetchOutwardReport]);

  React.useEffect(() => {
    localStorage.setItem(
      PREFERENCE_KEY,
      JSON.stringify({
        selected: selectedFieldFilter,
        order: draggedFieldOrder,
      }),
    );
  }, [draggedFieldOrder, selectedFieldFilter]);

  const toggleOrganizer = (open) => () => setOpenOrganizer(open);

  const handleSelectedFieldFilter = (event, value) => {
    setSelectedFieldFilter((current) =>
      event.target.checked
        ? [...new Set([...current, value])]
        : current.filter((field) => field !== value),
    );
  };

  const plantOptions =
    plantsData?.map((plantObj) => ({
      value: plantObj.id,
      label: plantObj.name,
    })) || [];

  const plantValue =
    plantOptions.find(
      (option) => String(option.value) === String(formData.plantId),
    ) || '';

  const workflowOptions =
    workflows?.map((workflowObj) => ({
      value: workflowObj.id,
      label: workflowObj.name,
    })) || [];

  const workflowValue =
    workflowOptions.find(
      (option) => String(option.value) === String(formData.workflowId),
    ) || '';

  const handleDateFilterChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handlePlantFilterChange = (selectedOption) => {
    setFormData((current) => ({
      ...current,
      plantId: selectedOption?.value || '',
    }));
  };

  const handleWorkflowFilterChange = (selectedOption) => {
    setFormData((current) => ({
      ...current,
      workflowId: selectedOption?.value || '',
    }));
  };

  const handleResetFilter = () => {
    setFormData(emptyFilters);
    setAppliedFilters(emptyFilters);
    setHasAppliedFilters(false);
    setPageNumber(1);
  };

  const handleApplyFilter = () => {
    setAppliedFilters(formData);
    setHasAppliedFilters(true);
    setPageNumber(1);
    setShowFunnel(false);
  };

  const exportToExcel = async () => {
    if (!rows.length) return;
    const { utils, write, saveAs } = await loadExcelExportModules();
    const excelRows = rows.map((row) => {
      const excelRow = {};
      tableHeaders.forEach((header) => {
        const cellValue = row[header.value];
        if (Array.isArray(cellValue)) {
          excelRow[header.label] = cellValue.length
            ? cellValue.join(', ')
            : '-';
        } else if (cellValue && typeof cellValue === 'object') {
          excelRow[header.label] = cellValue.name ?? '-';
        } else if (typeof cellValue === 'boolean') {
          excelRow[header.label] = cellValue ? 'Yes' : 'No';
        } else {
          excelRow[header.label] = cellValue ?? '-';
        }
      });
      return excelRow;
    });
    const worksheet = utils.json_to_sheet(excelRows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'OutwardReport');
    const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      `OutwardReportWithExitTime_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  const handleInputChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setPageNumber(value);
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) setPageNumber(pageNumber - 1);
  };

  const handleNextPage = () => {
    if (pageNumber < totalPages) setPageNumber(pageNumber + 1);
  };

  const formattedPageNumber = pageNumber < 10 ? `0${pageNumber}` : pageNumber;
  const hasUnappliedFilters = () =>
    Object.keys(formData).some((key) => formData[key] !== appliedFilters[key]);

  return (
    <div className="div-flex-column" data-testid="outward-report-page">
      <div className="page-layout div-flex-column">
        <div className="div-space-between slider-padding left-padding">
          <div className="div-flex-row cg-5">
            <Breadcrumb />
          </div>
          <Col xxl={2} xl={2} lg={4} md={5} sm={7} xs={7} className="p-0 m-0">
            <div className="input-wrapper">
              <input
                type="text"
                data-testid="outward-report-vehicle-search"
                className="form-input-section input-icon-remove"
                placeholder="Search vehicle number"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
              <span className="form-input-section-icon">
                <Search
                  className="icon-size-lg"
                  color={getColorCode(themeColor)}
                />
              </span>
            </div>
          </Col>
        </div>
        <div className="div-space-between border-bottom slider-padding">
          <span className="text-xxl text-bold left-padding">
            Outward Report
          </span>
          <div className="div-flex-row div-align-center cg-20">
            <div className="div-flex-row cg-20 div-align-center">
              <div className="list-container div-flex-column rg-5">
                <CustomToolTip title="Filter" placement="bottom">
                  <button
                    data-testid="filter-open-btn"
                    className="icon-btn-none"
                    aria-label="filter"
                    onClick={() => {
                      setShowFunnel((current) => !current);

                      if (isMobile) {
                        setShowFunnel(true);
                      }
                    }}
                  >
                    <Funnel
                      className="icon-size-lg"
                      color={showFunnel ? getColorCode(themeColor) : '#9F9F9F'}
                    />
                  </button>
                </CustomToolTip>
              </div>
              <div className="button-container">
                <CustomToolTip title="Organiser" placement="bottom">
                  <button
                    data-testid="organizer-open-btn"
                    className="icon-btn-none"
                    aria-label="organizer"
                    onClick={toggleOrganizer(true)}
                  >
                    <BulletPoints className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              </div>
              <Drawer
                anchor="right"
                open={openOrganizer}
                onClose={toggleOrganizer(false)}
                ModalProps={{ keepMounted: false }}
                sx={{ zIndex: 2 }}
              >
                <div className="right-drawer-component-small">
                  <TableRightDrawer
                    toggleDrawer={toggleOrganizer}
                    fieldFilter={fieldFilter}
                    selectedFieldFilter={selectedFieldFilter}
                    fieldOrder={effectiveFieldOrder}
                    setDraggedFieldOrder={setDraggedFieldOrder}
                    handleSelectedFieldFilter={handleSelectedFieldFilter}
                  />
                </div>
              </Drawer>
              <div className="button-container">
                <CustomToolTip title="Export Excel" placement="bottom">
                  <button
                    aria-label="export excel"
                    className="icon-btn-none"
                    onClick={exportToExcel}
                  >
                    <ExcelIcon className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              </div>
              {!isMobile && (
                <CustomToolTip title="Refresh" placement="bottom">
                  <button
                    aria-label="refresh"
                    className="icon-btn-none"
                    onClick={() => setRefreshKey((current) => current + 1)}
                  >
                    <Refresh className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              )}
            </div>
            {!isMobile && (
              <select
                value={paginationBatchSize}
                onChange={(event) => {
                  setPaginationBatchSize(Number(event.target.value));
                  setPageNumber(1);
                }}
                className="filter-input-section"
              >
                {[10, 20, 30, 40, 50, 100, 500, 1000].map((size) => (
                  <option value={size} key={size}>
                    {size}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        {isMobile && (
          <Drawer
            anchor="right"
            open={showFunnel}
            onClose={() => setShowFunnel(false)}
            ModalProps={{
              keepMounted: false,
            }}
            sx={{ zIndex: 2 }}
          >
            <div className="right-drawer-component-small">
              <OutwardReportMobileFilterDrawer
                formData={formData}
                handleDateFilterChange={handleDateFilterChange}
                handlePlantFilterChange={handlePlantFilterChange}
                handleWorkflowFilterChange={handleWorkflowFilterChange}
                handleResetFilter={handleResetFilter}
                handleApplyFilter={handleApplyFilter}
                closeFilter={() => setShowFunnel(false)}
                plantOptions={plantOptions}
                plantValue={plantValue}
                workflowOptions={workflowOptions}
                workflowValue={workflowValue}
                tenantOrganisationId={tenantOrganisationId}
              />
            </div>
          </Drawer>
        )}
        <Row className="g-0">
          <Col
            xxl={showFunnel ? 10 : 12}
            xl={showFunnel ? 9 : 12}
            lg={showFunnel ? 8 : 12}
            md={showFunnel ? 8 : 12}
            sm={showFunnel ? 0 : 12}
            xs={showFunnel ? 0 : 12}
            className="border-bottom"
          >
            <Table
              headers={tableHeaders}
              minCellWidth={150}
              tableContent={
                <OutwardReportTableContent headers={tableHeaders} rows={rows} />
              }
            />
            <div className="footer-container">
              <div className="div-space-between div-align-center footer-content-for-page">
                <div className="div-flex-row cg-20 pagination-container div-align-center">
                  <div className="div-flex-row cg-10 div-align-center">
                    <button
                      data-testid="pagination-prev"
                      className="icon-btn-none"
                      onClick={handlePreviousPage}
                      aria-label="previous page"
                    >
                      <ArrowLeft
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                    <input
                      className="page-number-container input-icon-remove text-sm"
                      data-testid="pagination-input"
                      value={formattedPageNumber}
                      onChange={handleInputChange}
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      max={totalPages}
                    />
                    <button
                      data-testid="pagination-next"
                      className="icon-btn-none"
                      onClick={handleNextPage}
                      aria-label="next page"
                    >
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                  </div>
                  <span className="text-grey text-sm">
                    Page {pageNumber} of {totalPages}
                  </span>
                </div>
              </div>
            </div>
          </Col>
          {showFunnel && !isMobile && (
            <Col
              xxl={showFunnel ? 2 : 0}
              xl={showFunnel ? 3 : 0}
              lg={showFunnel ? 4 : 0}
              md={showFunnel ? 4 : 0}
              sm={showFunnel ? 12 : 0}
              xs={showFunnel ? 12 : 0}
              className="border-left border-bottom"
            >
              <div
                className="filter-list-item div-flex-row div-space-between div-align-center border-bottom"
                data-testid="filter-drawer"
              >
                <span className="text-sm text-semi-bold">Filter</span>
                {hasUnappliedFilters() && (
                  <div className="text-yellow text-xs text-semi-bold">
                    ⚠️ Unapplied Filters
                  </div>
                )}
              </div>
              <div className="div-flex-column div-space-between rg-20">
                <div className="filter-list-item-container-scroll div-flex-column">
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Start Date</span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate || ''}
                        onChange={handleDateFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">End Date</span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="endDate"
                        value={formData.endDate || ''}
                        onChange={handleDateFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Plant</span>
                    <div className="input-wrapper">
                      <FilterMultiSelect
                        isMulti={false}
                        name="plantId"
                        className="multi-select-input-section"
                        value={plantValue}
                        options={plantOptions}
                        classNamePrefix="react-select"
                        placeholder="Select Plant"
                        onFocus={() =>
                          dispatch(getPlants(tenantOrganisationId))
                        }
                        onChange={handlePlantFilterChange}
                      />
                      <span className="form-input-section-icon">
                        <ArrowRight
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                          style={{ transform: 'rotate(90deg)' }}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Workflow</span>
                    <div className="input-wrapper">
                      <FilterMultiSelect
                        isMulti={false}
                        name="workflowId"
                        className="multi-select-input-section"
                        value={workflowValue}
                        options={workflowOptions}
                        classNamePrefix="react-select"
                        placeholder="Select Workflow"
                        onFocus={() =>
                          dispatch(
                            getVehicleTrackingWorkflows(
                              tenantOrganisationId,
                              '2',
                            ),
                          )
                        }
                        onChange={handleWorkflowFilterChange}
                      />
                      <span className="form-input-section-icon">
                        <ArrowRight
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                          style={{ transform: 'rotate(90deg)' }}
                        />
                      </span>
                    </div>
                  </div>
                </div>
                <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                  <button
                    className={`common-btn-outlined border-${themeColor}`}
                    onClick={handleResetFilter}
                    data-testid="filter-reset-btn"
                  >
                    Reset
                  </button>
                  <button
                    className={`common-btn theme-background-${themeColor}`}
                    onClick={handleApplyFilter}
                    data-testid="filter-apply-btn"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
}
